import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { RESTAURANTS } from "../../constants";
import ProductImagePicker from "./ProductImagePicker";
import ProductAssignment from "./ProductAssignment";

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">{label}</Label>
      {children}
      {hint && <p className="text-xs text-[#0B1F3A]/55 mt-1">{hint}</p>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 bg-white border border-[#E6E0D4] rounded-lg px-3 py-2">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span className="text-sm text-[#0B1F3A]">{label}</span>
    </label>
  );
}

// All the input controls for the ProductDialog form. Pure presentational —
// owns no state, just renders the current `form` and propagates patches.
export default function ProductFormFields({
  form,
  imgMode,
  set,
  toggleIn,
  suppliers,
  categories,
  user,
  canPickAnyRestaurant,
}) {
  return (
    <div className="space-y-4 pt-2">
      <ProductImagePicker
        image={form.image}
        initialMode={imgMode}
        onChange={(image) => set({ image })}
      />

      <Field label="Name">
        <Input
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g., Whole Milk, 1 gal"
          className="mt-1 bg-white border-[#E6E0D4]"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <Select value={form.category} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>
              {categories.length === 0 && (
                <SelectItem value="__none" disabled>No categories — add one in the Categories tab</SelectItem>
              )}
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Unit">
          <Input
            value={form.unit}
            onChange={(e) => set({ unit: e.target.value })}
            placeholder="ea · lb · case"
            className="mt-1 bg-white border-[#E6E0D4]"
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Vendor">
          <Select value={form.supplierId} onValueChange={(v) => set({ supplierId: v })}>
            <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} · {s.type === "delivered" ? "Delivered" : "Direct"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Par level">
          <Input
            type="number"
            min={0}
            value={form.par}
            onChange={(e) => set({ par: e.target.value })}
            className="mt-1 bg-white border-[#E6E0D4]"
          />
        </Field>
      </div>

      <Field label="Cost per unit (USD)" hint="Used by the finance dashboard to compute spend per vendor / month.">
        <Input
          data-testid="product-cost-input"
          type="number"
          min={0}
          step="0.01"
          value={form.costPerUnit}
          onChange={(e) => set({ costPerUnit: e.target.value })}
          placeholder="0.00"
          className="mt-1 bg-white border-[#E6E0D4]"
        />
      </Field>

      <ProductAssignment
        assignedRestaurants={form.assignedRestaurants}
        assignedAreas={form.assignedAreas}
        canPickAnyRestaurant={canPickAnyRestaurant}
        userRestaurant={user?.restaurant}
        onToggleRestaurant={(id) => toggleIn("assignedRestaurants", id)}
        onToggleArea={(id) => toggleIn("assignedAreas", id)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ToggleRow label="Available for purchase" checked={!!form.availableForPurchase} onChange={(v) => set({ availableForPurchase: v })} />
        <ToggleRow label="Available for transfer" checked={!!form.availableForTransfer} onChange={(v) => set({ availableForTransfer: v })} />
        <ToggleRow label="Active" checked={!!form.active} onChange={(v) => set({ active: v })} />
      </div>

      {canPickAnyRestaurant && (
        <Field label="Owner scope">
          <Select value={form.ownerRestaurant} onValueChange={(v) => set({ ownerRestaurant: v })}>
            <SelectTrigger className="mt-1 bg-white border-[#E6E0D4]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global (both restaurants)</SelectItem>
              {RESTAURANTS.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name} only</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    </div>
  );
}
